import { useMemo, useState } from 'react';
import { Check, X, Lock, Info, ShieldAlert, Thermometer } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useToleranceStore } from '../../stores/toleranceStore';
import { useAuthStore, selectIsFree } from '../../stores/authStore';
import { calculateWorstCase, calculateRSS } from '../../utils/calculations';
import { activeElements } from '../../utils/elementShape';
import { fmt, fmtSigned } from './measure/numFormat';
import BeitragsLeister from './results/BeitragsLeister';
import Histogram from './results/Histogram';
import MonteCarloStats from './results/MonteCarloStats';
import WissenHinweis, { WissenArtikelLink } from './WissenHinweis';

// ResultsPanel (Phase 4) — replaces the legacy src/components/Results
// Panel.jsx. New visual language per the handoff prototype:
//
//   - Hero card (dark gradient) with the *primary* result for the
//     currently-active method (WC / RSS / MC), an inline VerdictBadge
//     (UNBEWERTET / OK / NOK with UTG/OTG delta detail), and inline
//     Soll-Grenzen inputs so the user can set limits without leaving
//     the panel.
//   - Two method-comparison stat tiles (WC + RSS), always shown so
//     the user can read both numbers regardless of active mode. The
//     active method's tile is tinted brand-blue.
//   - BeitragsLeister: per-element % contribution to the closure.
//   - Histogram (MC only): empirical distribution + mean + WC bounds.
//
// Reads everything from the toleranceStore. WC and RSS get recomputed
// here (cheap) so the comparison tiles aren't gated on `mode`. The MC
// histogram data piggybacks on the store's m0.mc (only populated when
// mode === 'montecarlo').

const selectResultsState = (s) => ({
  elements: s.elements,
  m0: s.m0,
  mode: s.mode,
  setM0Limits: s.setM0Limits,
  preview: s.preview,
  counted: s.counted,
  // thermal-Block wird ans Hero + an die Comparison-Tile-Berechnungen
  // durchgereicht. Wenn enabled=false: keine Verhaltensänderung
  // gegenüber dem Pre-Task-#27-Stand.
  thermal: s.thermal,
});

// ─── Verdict + helpers ─────────────────────────────────────────────

// Mirrors the prototype's computeVerdict (handoff app.jsx:639). Returns
// status ('none' | 'ok' | 'nok') plus the signed delta for the broken
// side(s) so the NOK strip can show "Δ +0,012" without forcing the
// user to subtract numbers by hand.
function computeVerdict(min, max, limits) {
  const hasMin = limits && limits.min != null && limits.min !== '';
  const hasMax = limits && limits.max != null && limits.max !== '';
  if (!hasMin && !hasMax) return { status: 'none', deltaUtg: null, deltaOtg: null };
  const lo = hasMin ? Number(limits.min) : -Infinity;
  const hi = hasMax ? Number(limits.max) : Infinity;
  const utgBroken = hasMin && min < lo;
  const otgBroken = hasMax && max > hi;
  if (!utgBroken && !otgBroken) return { status: 'ok', deltaUtg: null, deltaOtg: null };
  return {
    status: 'nok',
    deltaUtg: utgBroken ? min - lo : null,   // negative → below
    deltaOtg: otgBroken ? max - hi : null,   // positive → above
  };
}

function VerdictBadge({ status }) {
  const CFG = {
    none: { label: 'UNBEWERTET', bg: 'rgba(255,255,255,0.10)', fg: 'rgba(255,255,255,0.65)', dot: 'rgba(255,255,255,0.55)', Icon: null },
    ok:   { label: 'OK',          bg: 'rgba(34,197,94,0.18)',  fg: '#86efac',               dot: '#34d399',                 Icon: Check },
    nok:  { label: 'NOK',         bg: 'rgba(239,68,68,0.18)',  fg: '#fca5a5',               dot: '#f87171',                 Icon: X },
  };
  const c = CFG[status] || CFG.none;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: c.bg, color: c.fg,
        fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6,
        padding: '3px 9px', borderRadius: 999, textTransform: 'uppercase',
      }}
    >
      {c.Icon
        ? <c.Icon size={11} aria-hidden="true" />
        : <span style={{ width: 6, height: 6, background: c.dot, borderRadius: '50%' }} />}
      {c.label}
    </span>
  );
}

// Soll-Grenzen input. Each side is independent — empty = "no limit on
// this side". Local draft state via the render-time prop-watch pattern
// to avoid a useEffect setState (same lint rule as MeasureTable's
// NumInput).
// Width is capped per-input (max 130 px) so the two side-by-side
// limits don't sprawl across the hero card. User feedback: previously
// each input ate ~190 px which felt oversized for typical 2-3 digit
// values like "0" / "200".
function LimitInput({ label, value, onCommit, placeholder = 'kein Limit' }) {
  const initial = value == null ? '' : String(value).replace('.', ',');
  const [draft, setDraft] = useState(initial);
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    setDraft(value == null ? '' : String(value).replace('.', ','));
  }
  const commit = () => {
    if (draft.trim() === '') { onCommit(null); return; }
    const n = parseFloat(draft.replace(',', '.'));
    onCommit(Number.isFinite(n) ? n : null);
  };
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '0 1 130px', maxWidth: 130 }}>
      <span
        style={{
          fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5,
          color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace('.', ','))}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
        placeholder={placeholder}
        inputMode="decimal"
        className="num"
        style={{
          width: '100%',
          height: 30, padding: '0 10px',
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 6, color: '#fff',
          fontSize: 13, fontWeight: 600, outline: 'none',
        }}
      />
    </label>
  );
}

// Stacked-tolerance renderer for stat tiles. Symmetric → single ±line;
// asymmetric → two stacked signed lines coloured by sign so the eye
// reads direction without re-checking.
function TolValue({ upper, lower }) {
  const sym = Math.abs(upper + lower) < 1e-6;
  if (sym) return <span className="num">±{fmt(Math.abs(upper), 3)}</span>;
  return (
    <span className="num" style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.05, fontSize: '0.85em' }}>
      <span style={{ color: upper >= 0 ? 'var(--color-ok)' : 'var(--color-err)' }}>{fmtSigned(upper, 3)}</span>
      <span style={{ color: lower >= 0 ? 'var(--color-ok)' : 'var(--color-err)' }}>{fmtSigned(lower, 3)}</span>
    </span>
  );
}

function StatTile({ label, upper, lower, sub, active }) {
  return (
    <div
      className={`bg-surface border ${active ? 'border-brand' : 'border-line'}`}
      style={{
        borderRadius: 'var(--radius-md)', padding: '12px 14px',
        display: 'flex', flexDirection: 'column', gap: 4, minHeight: 72,
      }}
    >
      <div
        className={active ? 'text-brand' : 'text-ink-3'}
        style={{ fontSize: 10.5, letterSpacing: 0.6, fontWeight: 600, textTransform: 'uppercase' }}
      >
        {label}
      </div>
      <div
        className={active ? 'text-brand' : 'text-ink'}
        style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.1 }}
      >
        <TolValue upper={upper} lower={lower} />
        <span className="text-ink-3" style={{ fontSize: 12, marginLeft: 4, fontWeight: 500 }}>mm</span>
      </div>
      {sub && <div className="num text-ink-3" style={{ fontSize: 11 }}>{sub}</div>}
    </div>
  );
}

// ─── ResultsPanel ──────────────────────────────────────────────────

export default function ResultsPanel() {
  const { elements, m0, mode, setM0Limits, counted, thermal, preview } = useToleranceStore(useShallow(selectResultsState));
  const isFree = useAuthStore(selectIsFree);
  const thermalActive = thermal?.enabled === true;

  // Always compute WC + RSS regardless of active mode — both tiles
  // are always shown. The store's m0 is the *active* method result;
  // for the comparison tiles we re-derive (cheap, sub-millisecond
  // for typical chains). Bei aktiviertem thermal-Modus rechnen wir
  // hier ebenfalls mit Tmin/Tmax-Envelope durch, damit beide Kacheln
  // konsistent zum Hero-Card-Wert sind.
  // C5: Vergleichskacheln rechnen wie der Store nur mit aktiven Maßen.
  const active = useMemo(() => activeElements(elements), [elements]);
  const wc = useMemo(() => calculateWorstCase(active, m0.limits, thermal), [active, m0.limits, thermal]);
  const rssR = useMemo(() => calculateRSS(active, m0.limits, thermal), [active, m0.limits, thermal]);

  if (!active.length) {
    // Minimal empty state — the MeasureTable's onboarding hint
    // already tells the user what to do, so this panel just shows
    // the Σ glyph + a short label.
    return (
      <div
        className="flex-1 text-ink-3"
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}
      >
        <div className="text-ink-4" style={{ fontSize: 64, fontWeight: 700, opacity: 0.18, letterSpacing: -2 }}>∑</div>
        <div className="text-ink-2" style={{ fontSize: 15, fontWeight: 600, marginTop: 6 }}>
          {elements.length ? 'Alle Maße sind deaktiviert' : 'Noch keine Ergebnisse'}
        </div>
      </div>
    );
  }

  // Free-tier gate: BERECHNEN flow (counted=true) must fire before
  // results show. Pro users auto-flip counted=true on first element,
  // so this branch never reaches them. The panel computes WC/RSS
  // itself for the comparison tiles, so without this check Free
  // would see results without ever consuming their monthly budget —
  // exactly the bug the user flagged ("ich kann ganz normal rechnen
  // ohne extra klicken zu müssen").
  if (isFree && !counted) {
    return (
      <div
        className="flex-1"
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}
      >
        <div
          className="bg-surface border border-line"
          style={{ width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}
        >
          <Lock size={22} className="text-warn" aria-hidden="true" />
        </div>
        <div className="text-ink-2" style={{ fontSize: 15, fontWeight: 600 }}>Bereit zum Rechnen</div>
        <div className="text-ink-3" style={{ fontSize: 12.5, marginTop: 4, maxWidth: 260, lineHeight: 1.5 }}>
          Klicke auf <strong className="text-ok">BERECHNEN</strong> im Header. Im Free-Plan verbraucht
          jeder Klick eine deiner monatlichen Berechnungen - danach siehst du das Ergebnis hier.
        </div>
        <Link
          to="/settings/billing"
          className="mt-4 text-[11px] font-bold text-brand hover:underline"
        >
          Unbegrenzt rechnen mit Pro →
        </Link>
      </div>
    );
  }

  // Primary result follows the active mode. Signed tolU / tolL (vs the
  // geometric nominal) come out via subtraction so unidirektionale
  // pairs (both above or both below) survive.
  let primary, primaryLabel, primaryTolU, primaryTolL;
  if (mode === 'rss') {
    primary = { nominal: rssR.nominal, min: rssR.min, max: rssR.max };
    primaryLabel = 'RSS (statistisch)';
    primaryTolU = rssR.max - rssR.nominal;
    primaryTolL = rssR.min - rssR.nominal;
  } else if (mode === 'montecarlo' && m0.mc) {
    primary = { nominal: m0.nominal, min: m0.min, max: m0.max };
    primaryLabel = 'Monte Carlo';
    primaryTolU = m0.max - m0.nominal;
    primaryTolL = m0.min - m0.nominal;
  } else {
    primary = { nominal: wc.nominal, min: wc.min, max: wc.max };
    primaryLabel = 'Worst Case';
    primaryTolU = wc.max - wc.nominal;
    primaryTolL = wc.min - wc.nominal;
  }
  const symmetric = Math.abs(primaryTolU + primaryTolL) < 1e-6;

  const verdict = computeVerdict(primary.min, primary.max, m0.limits);

  return (
    <div
      className="results-panel"
      style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, overflowY: 'auto', height: '100%' }}
    >
      {/* Section heading — same rhythm as MassketteDiagram + MeasureTable */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, paddingLeft: 4 }}>
        <h2 className="text-ink-2" style={{ fontSize: 13, fontWeight: 600, margin: 0, letterSpacing: 0.2, textTransform: 'uppercase' }}>
          Ergebnis
        </h2>
        <span className="text-ink-3" style={{ fontSize: 11 }}>
          Aktualisiert sich bei jeder Änderung
        </span>
      </div>

      {/* Hero card — primary result, verdict badge, limits inputs */}
      <div
        data-snippet="ergebnis-hero"
        style={{
          background: 'linear-gradient(180deg, #0c1426 0%, #0a1530 100%)',
          color: '#fff',
          borderRadius: 'var(--radius-lg)',
          padding: '16px 18px',
          position: 'relative', overflow: 'hidden',
          boxShadow: '0 1px 0 rgba(255,255,255,0.04) inset',
          flexShrink: 0,
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(600px 200px at 100% 0%, rgba(29,81,255,0.25), transparent 60%)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase' }}>
            Resultierendes Maß · {primaryLabel}
          </div>
          <VerdictBadge status={verdict.status} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, position: 'relative' }}>
          <div className="num" style={{ fontSize: 38, fontWeight: 600, letterSpacing: -1 }}>
            {fmt(primary.nominal, 2)}
          </div>
          <div className="num" style={{ fontSize: 18, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
            {symmetric ? (
              <>± {fmt(Math.abs(primaryTolU), 3)}</>
            ) : (
              <span style={{ display: 'inline-flex', flexDirection: 'column', fontSize: 13, lineHeight: 1.1 }}>
                <span style={{ color: primaryTolU >= 0 ? '#86efac' : '#fca5a5' }}>{fmtSigned(primaryTolU, 3)}</span>
                <span style={{ color: primaryTolL >= 0 ? '#86efac' : '#fca5a5' }}>{fmtSigned(primaryTolL, 3)}</span>
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginLeft: 4 }}>mm</div>
        </div>

        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginTop: 14, position: 'relative' }}
        >
          {[
            { label: 'Min',    val: primary.min },
            { label: 'Max',    val: primary.max },
            { label: 'Spanne', val: primary.max - primary.min },
          ].map((s) => (
            <div key={s.label}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                {s.label}
              </div>
              <div className="num" style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{fmt(s.val, 3)}</div>
            </div>
          ))}
        </div>

        {/* Thermal-Detail (Task #27 Phase F): zweite Zeile bei aktivem
            Wärme-Modus mit der thermischen Verschiebung des Schließmaßes
            an beiden Temperaturenden, sodass der Konstrukteur sofort
            sieht, was vom oberen min/max thermisch bedingt ist und was
            aus den Fertigungstoleranzen kommt. m0.thermal stammt aus
            dem aktiven Berechnungslauf — bei WC/RSS aus calculations.js,
            bei MC steht's nicht im Worker-Result, deshalb derivieren
            wir hier aus dem wc-Recompute als Fallback. */}
        {thermalActive && (wc.thermal || m0.thermal) && (
          <div
            style={{
              marginTop: 14, paddingTop: 12,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              position: 'relative',
            }}
          >
            <span
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                color: 'rgba(134,197,255,0.85)', textTransform: 'uppercase',
              }}
            >
              <Thermometer size={11} aria-hidden="true" />
              Wärmeausdehnung
            </span>
            {(() => {
              const t = wc.thermal || m0.thermal;
              return (
                <>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                    Bereich <span className="num" style={{ color: 'rgba(255,255,255,0.85)' }}>{t.tMin}…{t.tMax} °C</span>
                    <span style={{ marginLeft: 4, opacity: 0.6 }}>(Bezug 20 °C)</span>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      bei T<sub>min</sub>
                    </span>
                    <span className="num" style={{ fontSize: 12, fontWeight: 600, color: t.shiftAtTmin < 0 ? '#fca5a5' : '#86efac' }}>
                      {fmtSigned(t.shiftAtTmin, 3)}
                    </span>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      bei T<sub>max</sub>
                    </span>
                    <span className="num" style={{ fontSize: 12, fontWeight: 600, color: t.shiftAtTmax < 0 ? '#fca5a5' : '#86efac' }}>
                      {fmtSigned(t.shiftAtTmax, 3)}
                    </span>
                  </span>
                </>
              );
            })()}
          </div>
        )}

        {/* Hinweis-Streifen wenn thermal aktiv und MINDESTENS ein Maß
            kein Material gesetzt hat. Maße ohne Material gehen mit α=0
            in die Rechnung ein — bei teilweise gesetzten Materialien
            ist das selten gewollt (oft vergessen) und führt zu einem
            zu kleinen thermischen Anteil. Differenzierte Texte für
            „gar keins" vs. „teilweise" damit der Hinweis spezifisch
            bleibt. */}
        {(() => {
          if (!thermalActive || active.length === 0) return null;
          const missing = active.filter((el) => !el.material).length;
          if (missing === 0) return null;
          const all = missing === active.length;
          return (
            <div
              role="note"
              style={{
                marginTop: 12,
                padding: '8px 10px',
                background: 'rgba(245,158,11,0.10)',
                border: '1px solid rgba(245,158,11,0.30)',
                borderRadius: 6,
                fontSize: 11, lineHeight: 1.45, color: '#fcd34d',
                position: 'relative',
                display: 'flex', alignItems: 'flex-start', gap: 6,
              }}
            >
              <Info size={12} className="shrink-0" style={{ marginTop: 2 }} aria-hidden="true" />
              <span>
                {all ? (
                  <>
                    Wärmeausdehnung ist aktiv, aber <strong>kein Maß</strong> hat ein Material -
                    die thermische Verschiebung ist 0. Setze pro Zeile ein Material in der Spalte
                    „Material", damit der Effekt greift.
                  </>
                ) : (
                  <>
                    <strong>{missing} von {active.length}</strong> Maßen ohne Material - diese
                    werden mit α=0 gerechnet, der thermische Beitrag wird entsprechend unterschätzt.
                    Setze in der Spalte „Material" für jedes Maß den passenden Werkstoff.
                  </>
                )}
              </span>
            </div>
          );
        })()}

        {/* Soll-Grenzen inputs */}
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase' }}>
              Soll-Grenzen · M₀ <WissenHinweis id="sollGrenzen" ton="dunkel" />
            </span>
            {(m0.limits.min != null || m0.limits.max != null) && (
              <button
                type="button"
                onClick={() => setM0Limits({ min: null, max: null })}
                disabled={!!preview}
                title="Grenzen zurücksetzen"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  height: 22, padding: '0 8px',
                  background: 'transparent', color: 'rgba(255,255,255,0.55)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 999, cursor: 'pointer',
                  fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                }}
              >
                <X size={10} aria-hidden="true" /> Zurücksetzen
              </button>
            )}
          </div>
          <fieldset data-preview-lock disabled={!!preview} style={{ display: 'contents' }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <LimitInput
                label="Soll-Min (UTG)"
                value={m0.limits.min}
                onCommit={(v) => setM0Limits({ ...m0.limits, min: v })}
              />
              <LimitInput
                label="Soll-Max (OTG)"
                value={m0.limits.max}
                onCommit={(v) => setM0Limits({ ...m0.limits, max: v })}
              />
            </div>
          </fieldset>

          {/* NOK detail strip — only when a side fails. Shows the exact
              broken side + signed delta so the user sees the gap without
              subtracting in their head. */}
          {verdict.status === 'nok' && (
            <div
              role="alert"
              style={{
                display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 10,
                padding: '8px 10px',
                background: 'rgba(239,68,68,0.10)',
                border: '1px solid rgba(239,68,68,0.30)',
                borderRadius: 6, fontSize: 11, color: '#fca5a5',
              }}
            >
              {verdict.deltaUtg != null && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 800, letterSpacing: 0.4 }}>UTG</span>
                  <span className="num" style={{ fontWeight: 600 }}>
                    Min {fmt(primary.min, 3)} &lt; Soll {fmt(Number(m0.limits.min), 3)}
                  </span>
                  <span className="num" style={{ fontWeight: 700, color: '#fff' }}>
                    Δ {fmtSigned(verdict.deltaUtg, 3)}
                  </span>
                </span>
              )}
              {verdict.deltaOtg != null && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 800, letterSpacing: 0.4 }}>OTG</span>
                  <span className="num" style={{ fontWeight: 600 }}>
                    Max {fmt(primary.max, 3)} &gt; Soll {fmt(Number(m0.limits.max), 3)}
                  </span>
                  <span className="num" style={{ fontWeight: 700, color: '#fff' }}>
                    Δ +{fmt(Math.abs(verdict.deltaOtg), 3)}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Method comparison tiles (always shown) */}
      <div data-snippet="methoden" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <StatTile
          label="Worst Case"
          upper={wc.max - wc.nominal}
          lower={wc.min - wc.nominal}
          sub={`${fmt(wc.min, 3)} … ${fmt(wc.max, 3)}`}
          active={mode === 'worstcase'}
        />
        <StatTile
          label="RSS (Statistisch)"
          upper={rssR.max - rssR.nominal}
          lower={rssR.min - rssR.nominal}
          sub={`${fmt(rssR.min, 3)} … ${fmt(rssR.max, 3)}`}
          active={mode === 'rss'}
        />
      </div>

      <BeitragsLeister elements={elements} mode={mode} />

      {/* RSS-method assumption hint — short reminder of what the
          method assumes so users don't read it as a guaranteed bound.
          Only renders when RSS is the active method; WC/MC have their
          own implicit semantics (worst-bound / sampled). */}
      {mode === 'rss' && (
        <div
          role="note"
          className="bg-surface border border-line"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: '10px 12px', borderRadius: 'var(--radius-md)',
            fontSize: 11.5, lineHeight: 1.5,
          }}
        >
          <Info size={14} className="text-brand shrink-0" style={{ marginTop: 1 }} aria-hidden="true" />
          <span className="text-ink-2">
            <strong>RSS-Annahmen:</strong> unabhängige, normalverteilte Einzeltoleranzen. Das Ergebnis
            ist die <em>statistisch erwartete</em> Streuung - keine garantierten Grenzen wie bei
            Worst&nbsp;Case. <WissenArtikelLink id="rss" />
          </span>
        </div>
      )}

      {/* Monte-Carlo-only blocks. MonteCarloStats brings back the Cp /
          Cpk / σ / percentile + capability-verdict panel that lived in
          the pre-reboot ResultsPanel. Histogram renders the empirical
          distribution. Both gated on m0.mc so WC / RSS modes (which
          don't populate it) stay clean. */}
      {mode === 'montecarlo' && m0.mc && (
        <>
          <MonteCarloStats mc={m0.mc} />
          <Histogram mc={m0.mc} m0={m0} wc={wc} />
        </>
      )}

      {/* Liability disclaimer — discreet footer card. Replaces the
          pre-reboot long boilerplate ("Normen können sich jederzeit
          ändern…") with the two contractual statements the user
          actually wants surfaced: results follow from the inputs,
          no liability for outcomes. */}
      <div
        role="note"
        className="text-ink-3"
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '10px 12px',
          borderTop: '1px dashed var(--color-line)',
          marginTop: 4,
          fontSize: 10.5, lineHeight: 1.55,
        }}
      >
        <ShieldAlert size={12} className="shrink-0" style={{ marginTop: 1 }} aria-hidden="true" />
        <span>
          Die Ergebnisse basieren ausschließlich auf den eingegebenen Werten. Eine Haftung für die
          Berechnung und daraus resultierende Folgen ist ausgeschlossen.
        </span>
      </div>
    </div>
  );
}
